import { Badge } from '../../../../../widgets/badge';
import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { panel, PropertiesPanelSwatch } from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../../widgets/notificationToaster';
import React, { FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
// import { commitMutation } from 'react-relay'; // Commentato - usiamo 3D core
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
// import environment from 'src/relay/environment'; // Commentato - usiamo 3D core
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { MaterialsIcon as PaletteSolidIcon, ViewGridIcon, ViewListIcon } from '../../../../../widgets/icons/scene';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import {
    CamerasIcon as PreviewIcon,
    DuplicateIcon as CloneIcon,
    SelectedIcon as SelectAllIcon,
    FolderIcon,
    GroupIcon,
    ImportAssetIcon as ImportIcon,
    PlusIcon as NewIcon,
    SaveAssetIcon as SaveIcon,
    TrashIcon as RemoveIcon
} from '../../../../../widgets/icons/scene';
import MaterialImportDialog from './MaterialImportDialog';
import MaterialOverlaySimple from './MaterialOverlaySimple';
import { useMaterialPreviews } from './hooks/useMaterialPreviews';
import { materialSwatchFallback } from './materialPreview';
// import { createLibraryMaterialMutation } from './query/createLibraryMaterialMutation'; // Commentato - usiamo 3D core

import { BaseTreeNode, markMatches, TreeComponent, TreeHandle, TreeTwisty, useTreeNodeManager } from '../tree';
import { freeName, useBornSubject } from './hooks/useBornSubject';
import {
    AssetCard,
    AssetCardActions,
    AssetCardSurface,
    AssetEmptyState,
    AssetGrid,
    AssetGroupCount,
    AssetGroupGlyph,
    AssetGroupName,
    AssetGroupRow,
    AssetGroupTitle,
    AssetName,
    AssetNameInput,
    AssetSwatch
} from './assetLibraryPanel';
import { useInlineRename } from '../useInlineRename';
import SectionPanel from './sectionPanel';
import { useRemovalPrompt } from './confirmRemoval';
import { SceneEditorAction } from './sceneEditorActionBar';
import CommandMenu from './commandMenu';
import SelectionInspector from './selectionInspector';
import { ResourceListViewMode, ResourceListViewModeSwitch } from '../../../../../widgets/resourceList';

// La stessa sfera della tessera, alla misura di una riga.

export interface MaterialNode extends BaseTreeNode {
    // Proprietà base di BaseTreeNode (id, name, children) sono ereditate
    type: 'material' | 'group'; // Indica il tipo di nodo
    customIcon?: React.ReactNode; // Icona personalizzata per il nodo
    notTurnableIntoFolder?: boolean; // Impedisce la trasformazione in cartella
    visible?: boolean; // Indica se il nodo è visibile
    data: {
        // L'oggetto materiale BABYLON completo per accesso diretto
        material?: any;
        // Proprietà scomposte per compatibilità
        variantId?: number;
        materialType?: string;
        diffuseColor?: string;
        specularColor?: string;
        emissiveColor?: string;
        roughness?: number;
        metallic?: number;
        // Proprietà dal sceneEditor
        _internalId?: string;
        enabled?: boolean;
        visible?: boolean;
        // Per i gruppi
        groupName?: string;
        materialIds?: string[]; // Array di ID dei materiali nel gruppo
    };
}

const UpgradeBanner = styled(UpgradeDialog)`
    width: 400px;
    height: 300px;
`;

interface MaterialsEditorProps {
    materials?: any[];
    selectedMaterials?: any[];
    sceneEditor?: any;
    zkScene?: any;
    onMaterialsUpdated?: (materials: any[]) => void;
    treeRef: React.RefObject<TreeHandle>;
    additionalFeatures?: any;
    onInspectorChange?: (component: React.ReactNode | null) => void;
    /** Porta la colonna di sinistra sugli oggetti: la pastiglia «4 objects» di un materiale. */
    onRevealObject?: (meshID: string) => void;
    /**
     * «DOVE STA?» CHIESTO DA UN'ALTRA SEZIONE. Chi lo chiede conosce il `dataID` della cosa — l'id
     * della mesh o del materiale — e non quello della riga, che è una faccenda interna a questo
     * elenco. La traduzione la fa chi possiede l'albero, cioè noi.
     */
    whereRequest?: { dataID: string; token: number } | null;
    /** La scena è cambiata con un gesto che non passa da un campo — il commutatore ORM/Separate. */
    onSceneChanged?: () => void;
}

const MaterialsEditor: FC<MaterialsEditorProps> = ({
    treeRef,
    materials = [],
    selectedMaterials = [],
    sceneEditor,
    zkScene,
    onMaterialsUpdated,
    additionalFeatures,
    onInspectorChange,
    onRevealObject,
    whereRequest,
    onSceneChanged
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { subscription, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // Ricevi materiali dal SceneEditor via props

    // State per il refresh trigger (come objectEditor)
    // Stessa ragione delle luci: un interruttore perde i segnali, un contatore no.
    const [materialRefresh, setMaterialRefresh] = useState(0);
    const refreshMaterials = useCallback(() => setMaterialRefresh((n) => n + 1), []);

    // State locale per i materiali (come objectEditor con meshes)
    const [localMaterials, setLocalMaterials] = useState<any[]>(() => {
        const initialMaterials = sceneEditor?.getAllMaterials() || materials || [];
        return initialMaterials;
    });

    // Le anteprime non si generano da sole: il pannello propone, l'utente decide. Il motore è
    // qui, e lo usano sia il comando in barra sia la dialog che compare entrando.
    const { previews, setPreview, generatePreviews, missing, progress, isGenerating } = useMaterialPreviews(sceneEditor, localMaterials);

    // `previews` tiene sia gli scatti di questa sessione sia gli URL già salvati che la coda ha
    // ritrovato sulla scena: chi disegna un materiale guarda un posto solo. Sta **qui**, accanto
    // alla coda che lo riempie, perché lo leggono sia la tessera sia la riga dell'elenco — e la
    // riga lo legge dentro un `useMemo`, che gira durante il render: dichiararlo più in basso lo
    // renderebbe irraggiungibile proprio a chi lo chiede per primo.
    const previewOf = useCallback(
        (node: MaterialNode) => {
            const materialId = node.data?.material?.id ?? node.data?.material?.name ?? node.id;
            return previews[materialId] ?? null;
        },
        [previews]
    );

    // Generare le anteprime è lavoro lungo e visibile: si dice quando comincia, quante sono e
    // com'è finita. Il conto avanza nel tooltip del comando.
    const runPreviewGeneration = useCallback(
        async (targets: any[]) => {
            if (targets.length === 0) return;

            const notificationId = showInfoNotification({
                message: stringFormat(T._('Generating {0} material preview(s)...', 'Admin'), targets.length.toString())
            });

            const generated = await generatePreviews(targets);
            removeNotification(notificationId);

            if (generated > 0) {
                showSuccessNotification({ message: stringFormat(T._('{0} material preview(s) generated', 'Admin'), generated.toString()) });
            } else {
                showErrorNotification({ message: T._('No material preview could be generated.', 'Admin') });
            }
        },
        [generatePreviews]
    );

    // State per gestire il displayOrder dei gruppi (come nel vecchio SceneEditorPage)
    const [groupMaterialsDisplayOrder, setGroupMaterialsDisplayOrder] = useState<Map<string, number>>(new Map());

    // Set di materiali che sono stati manualmente disassociati dai gruppi
    const [manuallyUngroupedMaterials, setManuallyUngroupedMaterials] = useState<Set<string>>(new Set());

    // Ref per il flag di importazione (per accesso immediato, non asincrono come useState)

    // Ref per tenere i nodi correnti durante l'import
    const currentNodesRef = useRef<MaterialNode[]>([]);

    // Ref per il flag di drag in corso (per evitare refresh durante il drag)
    const isDraggingRef = useRef(false);

    // Converti i materiali dal SceneEditor in MaterialNode per il tree
    const convertToMaterialNodes = useCallback(
        (sceneMaterials: any[]): MaterialNode[] => {
            // Usa getGroupedMaterials() se disponibile per avere la struttura corretta con i gruppi
            if (sceneEditor && sceneEditor.getGroupedMaterials) {
                try {
                    const groupedMaterials = sceneEditor.getGroupedMaterials();

                    const result: MaterialNode[] = [];
                    const materialsToForceInRoot: any[] = []; // Materiali da forzare nella radice

                    groupedMaterials.forEach((group: any) => {
                        // Il gruppo "dummy" contiene materiali non raggruppati - non mostrare il gruppo stesso
                        if (group.groupName === '_dummy_' || group.groupName === '' || !group.groupName) {
                            // Materiali del gruppo dummy - aggiungili direttamente alla radice (senza gruppo visibile)
                            if (group.materials) {
                                group.materials.forEach((material: any, index: number) => {
                                    // Assicurati che il materiale abbia metadata
                                    if (!material.metadata) {
                                        material.metadata = {};
                                    }
                                    // Se non ha displayOrder, assegnalo basato sull'index
                                    if (material.metadata.displayOrder === undefined) {
                                        material.metadata.displayOrder = index;
                                    }

                                    result.push({
                                        id: material._internalId || material.id || `material-${index}`,
                                        name: material.name || `Material ${index + 1}`,
                                        type: 'material' as const,
                                        children: [],
                                        customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />,
                                        notTurnableIntoFolder: true,
                                        data: {
                                            material: material,
                                            variantId: material.variantId || 1,
                                            materialType: material.materialType || 'pbr',
                                            diffuseColor: material.diffuseColor || '#FFFFFF',
                                            specularColor: material.specularColor || '#FFFFFF',
                                            emissiveColor: material.emissiveColor || '#000000',
                                            roughness: material.roughness ?? 0.5,
                                            metallic: material.metallic ?? 0.0,
                                            _internalId: material._internalId,
                                            enabled: material.enabled ?? true,
                                            visible: material.visible ?? true
                                        }
                                    });
                                });
                            }
                        } else {
                            // Gruppi reali (non dummy) - crea un nodo gruppo visibile

                            // FILTRA i materiali manualmente disassociati da questo gruppo
                            const groupMaterials =
                                group.materials?.filter((material: any) => {
                                    // Usa la stessa logica di generazione ID dei nodi TreeComponent
                                    const materialId = material._internalId || material.id;
                                    const isManuallyUngrouped = manuallyUngroupedMaterials.has(materialId);
                                    if (isManuallyUngrouped) {
                                        materialsToForceInRoot.push(material);
                                    }
                                    return !isManuallyUngrouped;
                                }) || [];

                            // Se il gruppo non ha più materiali, saltalo
                            if (groupMaterials.length === 0) {
                                return;
                            }

                            const groupNode: MaterialNode = {
                                id: `group-${group.guid || Date.now()}`,
                                name: group.groupName,
                                type: 'group' as const,
                                children: [],
                                customIcon: <FolderIcon style={{ width: '16px', height: '16px', fill: panel.kindGroupInk }} />,
                                visible: true, // I gruppi sono sempre visibili
                                data: {
                                    groupName: group.groupName,
                                    materialIds: groupMaterials.map((m: any) => m.id)
                                }
                            };

                            // Aggiungi i materiali come figli del gruppo
                            if (groupMaterials.length > 0) {
                                groupNode.children = groupMaterials.map((material: any, index: number) => {
                                    // Assicurati che il materiale abbia metadata
                                    if (!material.metadata) {
                                        material.metadata = {};
                                    }
                                    // Per i materiali nei gruppi, il displayOrder è l'index del materiale nel gruppo
                                    if (material.metadata.displayOrder === undefined) {
                                        material.metadata.displayOrder = index;
                                    }

                                    return {
                                        id: material._internalId || material.id || `material-${index}`,
                                        name: material.name || `Material ${index + 1}`,
                                        type: 'material' as const,
                                        children: [],
                                        customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />,
                                        notTurnableIntoFolder: true,
                                        data: {
                                            material: material,
                                            variantId: material.variantId || 1,
                                            materialType: material.materialType || 'pbr',
                                            diffuseColor: material.diffuseColor || '#FFFFFF',
                                            specularColor: material.specularColor || '#FFFFFF',
                                            emissiveColor: material.emissiveColor || '#000000',
                                            roughness: material.roughness ?? 0.5,
                                            metallic: material.metallic ?? 0.0,
                                            _internalId: material._internalId,
                                            enabled: material.enabled ?? true,
                                            visible: material.visible ?? true
                                        }
                                    };
                                });
                            }

                            result.push(groupNode);
                        }
                    });

                    // Aggiungi i materiali manualmente disassociati dai gruppi alla radice
                    materialsToForceInRoot.forEach((material: any, index: number) => {
                        // Assicurati che il materiale abbia metadata
                        if (!material.metadata) {
                            material.metadata = {};
                        }
                        // Assegna un displayOrder elevato per metterli alla fine della lista
                        if (material.metadata.displayOrder === undefined) {
                            material.metadata.displayOrder = 1000 + index; // Elevato per metterli in fondo
                        }

                        const materialNode: MaterialNode = {
                            id: material._internalId || material.id || `material-ungrouped-${index}`,
                            name: material.name || `Material ${index + 1}`,
                            type: 'material' as const,
                            children: [],
                            customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />,
                            notTurnableIntoFolder: true,
                            data: {
                                material: material,
                                variantId: material.variantId || 1,
                                materialType: material.materialType || 'pbr',
                                diffuseColor: material.diffuseColor || '#FFFFFF',
                                specularColor: material.specularColor || '#FFFFFF',
                                emissiveColor: material.emissiveColor || '#000000',
                                roughness: material.roughness ?? 0.5,
                                metallic: material.metallic ?? 0.0,
                                _internalId: material._internalId,
                                enabled: material.enabled ?? true,
                                visible: material.visible ?? true
                            }
                        };

                        result.push(materialNode);
                    });

                    return result;
                } catch (error) {
                    console.warn('Error getting grouped materials, falling back to regular materials:', error);
                }
            }

            // Fallback alla vecchia logica se getGroupedMaterials non è disponibile
            const materialsToUse = sceneMaterials.length > 0 ? sceneMaterials : localMaterials;
            if (!materialsToUse || materialsToUse.length === 0) {
                return [];
            }

            return materialsToUse.map((material: any, index: number) => {
                // Assicurati che il materiale abbia metadata
                if (!material.metadata) {
                    material.metadata = {};
                }
                // Se non ha displayOrder, assegnalo basato sull'index
                if (material.metadata.displayOrder === undefined) {
                    material.metadata.displayOrder = index;
                }

                return {
                    id: material._internalId || material.id || `material-${index}`,
                    name: material.name || `Material ${index + 1}`,
                    type: 'material' as const, // Specifica che è un materiale
                    children: [],
                    customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />, // Icona custom per i materiali
                    notTurnableIntoFolder: true, // Impedisce che i materiali diventino cartelle nel drag&drop
                    data: {
                        // Salva l'oggetto materiale completo per accesso diretto
                        material: material,
                        // Mantieni anche le proprietà scomposte per compatibilità
                        variantId: material.variantId || 1,
                        materialType: material.materialType || 'pbr',
                        diffuseColor: material.diffuseColor || '#FFFFFF',
                        specularColor: material.specularColor || '#FFFFFF',
                        emissiveColor: material.emissiveColor || '#000000',
                        roughness: material.roughness ?? 0.5,
                        metallic: material.metallic ?? 0.0,
                        _internalId: material._internalId,
                        enabled: material.enabled ?? true,
                        visible: material.visible ?? true
                    }
                };
            });
        },
        [localMaterials, sceneEditor]
    );

    // Converti i MaterialNode di nuovo in formato SceneEditor
    const convertToSceneMaterials = useCallback((materialNodes: MaterialNode[]) => {
        return materialNodes.map((node) => {
            // Se abbiamo il materiale originale, lo utilizziamo come base
            if (node.data.material) {
                return {
                    ...node.data.material,
                    // Sovrascrivi con eventuali modifiche dal nodo
                    id: node.id,
                    name: node.name,
                    enabled: node.data.enabled,
                    visible: node.data.visible
                };
            }

            // Altrimenti, crea il materiale dalle proprietà scomposte
            return {
                _internalId: node.data._internalId,
                id: node.id,
                name: node.name,
                variantId: node.data.variantId,
                materialType: node.data.materialType,
                diffuseColor: node.data.diffuseColor,
                specularColor: node.data.specularColor,
                emissiveColor: node.data.emissiveColor,
                roughness: node.data.roughness,
                metallic: node.data.metallic,
                enabled: node.data.enabled,
                visible: node.data.visible
            };
        });
    }, []);

    // Ottieni i materiali iniziali convertiti - USA QUELLI DALLO SCENE EDITOR
    // Rimosso initialMaterialsData - ora usiamo direttamente convertToMaterialNodes(sceneEditorMaterials)

    // Hook per la gestione del tree con dati reali - MATERIALI MEMOIZZATI PER EVITARE LOOP
    const materialNodes = useMemo(() => {
        // Se stiamo importando, non ricalcolare - mantieni i nodi attuali

        const nodes = convertToMaterialNodes(localMaterials);

        // Implementa il sort come nel vecchio SceneEditorPage
        // Combina materiali e gruppi per un ordinamento globale
        const allItems: any[] = [];

        nodes.forEach((node) => {
            if (node.type === 'group') {
                // Per i gruppi, usa il displayOrder dalla Map
                const groupDisplayOrder = groupMaterialsDisplayOrder.get(node.data.groupName || '') ?? 0;
                allItems.push({ node, sortOrder: groupDisplayOrder, isGroup: true });
            } else if (node.type === 'material' && node.data.material) {
                // Per i materiali, usa metadata.displayOrder
                const materialDisplayOrder = node.data.material.metadata?.displayOrder ?? 0;
                allItems.push({ node, sortOrder: materialDisplayOrder, isGroup: false });
            }
        });

        // Ordina tutti gli elementi per sortOrder
        allItems.sort((a, b) => a.sortOrder - b.sortOrder);

        const sortedNodes = allItems.map((item) => item.node);

        return sortedNodes;
    }, [localMaterials, convertToMaterialNodes, groupMaterialsDisplayOrder, manuallyUngroupedMaterials]);

    // Ref per tenere riferimento alla funzione updateNode
    const updateNodeRef = useRef<((id: string, updates: Partial<MaterialNode>) => void) | null>(null);
    const treeStateRef = useRef<any>(null);

    // I COMANDI DI UN GRUPPO NASCONO PIÙ IN BASSO DI QUI — vogliono l'albero e `prepareMaterials`
    // — e questa fabbrica non può dipenderne: cambierebbe a ogni disegno, e l'Inspector riceverebbe
    // un elemento nuovo ogni volta. È la stessa soluzione dei due ref qui sopra.
    const groupCardRef = useRef<{
        open: (materialNodeId: string) => void;
        rename: (group: MaterialNode, name: string) => void;
        dissolve: (group: MaterialNode) => void;
    } | null>(null);

    // Funzione per creare il componente overlay
    const createMaterialOverlay = useCallback(
        (node: MaterialNode) => {
            return () => {
                // LA SCHEDA DI UN GRUPPO: il nome, i materiali che ci stanno dentro, e il solo
                // comando che un gruppo ha. Prima qui non si apriva niente — una cartella si
                // rinominava dal «…» della sua riga, e cosa contenesse lo diceva soltanto l'albero.
                //
                // È IL PANNELLO DEL MUCCHIO CON UN NOME SOPRA, e non un secondo componente: un
                // gruppo è un insieme di soggetti più i comandi che valgono per tutti, cioè
                // esattamente ciò che l'Inspector mostra quando prendi otto righe. Cambia che qui
                // l'insieme ha un nome proprio — che si cambia — e che ogni riga è una PORTA:
                // premerla entra in quel materiale (mockup, § 4).
                if (node.type === 'group') {
                    const materials = (node.children ?? []) as MaterialNode[];

                    return (
                        <SelectionInspector
                            kind='matgroup'
                            name={node.name}
                            specs={
                                materials.length === 1 ? T._('1 material', 'Admin') : stringFormat(T._('{0} materials', 'Admin'), materials.length.toString())
                            }
                            onRename={(value) => groupCardRef.current?.rename(node, value)}
                            subjectLabel={T._('materials', 'Admin')}
                            entries={materials.map((material) => ({
                                id: material.id,
                                name: material.name,
                                // LA PALLINA PORTA IL COLORE DEL SUO MATERIALE, e non la tinta della specie:
                                // qui i materiali sono pochi e si guardano uno accanto all’altro, quindi il colore
                                // distingue — dove in una lista di quaranta righe direbbe solo che sono diversi.
                                icon: <PropertiesPanelSwatch $tint={material.data?.diffuseColor || panel.kindAsset} $size={16} />,
                                onPick: () => groupCardRef.current?.open(material.id)
                            }))}
                            actions={[
                                {
                                    name: T._('Remove group', 'Admin'),
                                    tooltip: T._('The folder goes, the materials stay', 'Admin'),
                                    icon: <RemoveIcon />,
                                    kind: 'danger',
                                    onClick: () => groupCardRef.current?.dissolve(node)
                                }
                            ]}
                        />
                    );
                }

                // Usa il materiale BABYLON reale direttamente
                const material = node.data.material;

                if (!material) {
                    return <div>Material not found</div>;
                }

                // Callback per aggiornare immediatamente il nome del nodo nel tree
                const handleNameChanged = (newName: string) => {
                    if (updateNodeRef.current) {
                        updateNodeRef.current(node.id, { name: newName });
                    }
                };

                // Callback per chiudere l'overlay
                const handleClose = () => {
                    if (treeStateRef.current && treeStateRef.current.closeOverlay) {
                        treeStateRef.current.closeOverlay();
                    }
                };

                return (
                    <MaterialOverlaySimple
                        material={material}
                        onNameChanged={handleNameChanged}
                        additionalFeatures={additionalFeatures}
                        onClose={handleClose}
                        onPreviewUpdated={setPreview}
                        onRevealObject={onRevealObject}
                        onSceneChanged={onSceneChanged}
                    />
                );
            };
        },
        [additionalFeatures, setPreview, onRevealObject, onSceneChanged]
    );

    const {
        nodes,
        selectedIds,
        setSelectedIds,
        treeState,
        filter,
        setFilter,
        findNodeById,
        updateNode,
        cloneNodes,
        removeNodes,
        groupNodes,
        setNodes,
        filteredData
    } = useTreeNodeManager<MaterialNode>(materialNodes, createMaterialOverlay);

    // QUANTI OGGETTI PORTANO QUESTO MATERIALE. Il conto lo dà il motore — `getMeshesUsingMaterial`,
    // che guarda anche dentro i MultiMaterial — ed è lo stesso che apre la pastiglia nella testata
    // dell'Inspector (§ 15): due conteggi separati sarebbero due verità sullo stesso numero.
    //
    // SI CALCOLA UNA VOLTA PER ELENCO, non riga per riga: quella domanda gira su tutte le mesh
    // della scena, e farla a ogni ridisegno di ogni riga sarebbe lo stesso giro moltiplicato per
    // due. Si rifà quando i nodi cambiano, che è quando può essere cambiata la risposta.
    const usageByMaterial = useMemo(() => {
        const counts = new Map<string, number>();

        const walk = (list: MaterialNode[]) =>
            list.forEach((node) => {
                const id = node.data?.material?.id;
                if (id) counts.set(id, sceneEditor?.getMeshesUsingMaterial?.(id)?.length ?? 0);
                if (node.children) walk(node.children as MaterialNode[]);
            });

        walk(nodes);
        return counts;
    }, [nodes, sceneEditor]);

    // La vista di fabbrica è la griglia: un materiale si riconosce dalla superficie, e in
    // colonna una tessera dice più di un nome. L'elenco resta a un clic, ed è dove si
    // trascina per raggruppare.
    // LA LIBRERIA APRE IN ELENCO (utente, 2026-08-26). La griglia è fatta per le facce, e le facce
    // non ci sono finché qualcuno non le chiede: una griglia di riquadri vuoti è la stessa lista
    // con dentro molto più aria e molte meno righe in vista. In elenco il nome basta, e la pallina
    // dice di che specie è la riga.
    const [viewMode, setViewMode] = useState<ResourceListViewMode>('table');

    // CHIUDERE UN GRUPPO È DELLA VISTA, non del dato — e serve solo qui: in elenco lo stato aperto
    // lo tiene già l'albero. In griglia, dove il rientro non esiste, è l'unico modo di mostrare un
    // gruppo come una cosa sola: chiuso, i suoi non ci sono; aperto, vengono subito dopo di lui.
    const [closedGroups, setClosedGroups] = useState<Set<string>>(new Set());

    const toggleGroup = (groupId: string) =>
        setClosedGroups((current) => {
            const next = new Set(current);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });

    // CHIUDERE È UN GESTO SOLO, come aprire. E per un po' non lo è stato: `closeOverlay` parla
    // all'ALBERO, che possiede il pannello soltanto nella vista a elenco — in griglia un albero non
    // c’è e la scheda la pubblica la sezione. Chi toglieva un gruppo dalla griglia si ritrovava
    // quindi la scheda di una cartella che non esisteva più: il comando aveva chiuso una porta che
    // non era quella da cui era entrato.
    //
    // Si chiudono tutte e due, sempre: chiudere quella già chiusa non costa niente, e la sezione ha
    // il diritto di cancellare la colonna perché ciò che ci sta dentro è suo.
    const closeInspector = () => {
        treeState.closeOverlay();

        // In griglia la tessera accesa È il pannello aperto: chiuso quello, resterebbe accesa una
        // riga che non descrive più niente. In elenco la selezione sopravvive alla chiusura, ed è
        // come si è sempre comportata: da lì si continua a lavorare sulle righe prese.
        if (viewMode === 'cards') setSelectedIds([]);

        onInspectorChange?.(null);
    };

    // Aggiorna la ref con il treeState
    React.useEffect(() => {
        treeStateRef.current = treeState;
    }, [treeState]);

    // Funzione helper per trovare nodi che funziona sia con filteredData che con treeState originale
    const findNodeByIdUnified = useCallback(
        (nodeId: string): MaterialNode | null => {
            // Se c'è un filtro attivo, cerca prima nei filteredData
            if (filter && filteredData.length > 0) {
                const searchInFiltered = (nodes: MaterialNode[]): MaterialNode | null => {
                    for (const node of nodes) {
                        if (node.id === nodeId) return node;
                        if (node.children && node.children.length > 0) {
                            const found = searchInFiltered(node.children as MaterialNode[]);
                            if (found) return found;
                        }
                    }
                    return null;
                };

                const foundInFiltered = searchInFiltered(filteredData);
                if (foundInFiltered) return foundInFiltered;
            }

            // Se non trovato nei filteredData o non c'è filtro, cerca nel treeState originale
            const originalFound = findNodeById(nodeId);
            return originalFound || null;
        },
        [filter, filteredData, findNodeById]
    );

    // OVERRIDE del canTurnIntoFolder per permettere il drop materiale-su-materiale per il riordino
    const customCanTurnIntoFolder = useCallback(
        (node: MaterialNode) => {
            // I gruppi possono sempre essere target (per raggruppamento)
            if (node.type === 'group') {
                return true;
            }

            // I materiali possono essere target SOLO se:
            // 1. C'è un drag in corso
            // 2. Il nodo trascinato è un materiale (per il riordino)
            if (node.type === 'material' && treeState.draggedId) {
                const draggedNode = findNodeByIdUnified(treeState.draggedId);
                if (draggedNode?.type === 'material') {
                    return true; // Permetti il drop per il riordino
                }
            }

            // In tutti gli altri casi, usa la logica standard
            return !node.notTurnableIntoFolder;
        },
        [treeState.draggedId, findNodeByIdUnified]
    );

    // Aggiungi il componente overlay direttamente ai nodi del treeState (come objectEditor)
    useEffect(() => {
        treeState.nodes.forEach((node) => {
            node.component = createMaterialOverlay(node);
        });
    }, [treeState.nodes, createMaterialOverlay]);

    // Crea un treeState per il TreeComponent che usa filteredData quando c'è un filtro attivo
    // «DOVE STA?» — la riga si accende e si porta in vista, e non cambia né la selezione né il
    // pannello. Arriva per prop e non dal manico dell'albero: quel manico è uno per quattro sezioni,
    // e una chiamata imperativa poteva finire su un altro albero e non fare niente, in silenzio.
    const [blinkRequest, setBlinkRequest] = useState<{ nodeId?: string; dataID?: string; token: number } | null>(null);

    const askWhereItIs = useCallback((nodeId: string) => setBlinkRequest((previous) => ({ nodeId, token: (previous?.token ?? 0) + 1 })), []);

    // «DOVE STA?» CHIESTO DA FUORI: si gira all'albero così com'è arrivato. La traduzione da `dataID`
    // a riga la fa lui, che è l'unico a sapere quando il suo elenco è pronto.
    useEffect(() => {
        if (!whereRequest) return;

        setBlinkRequest((previous) => ({ dataID: whereRequest.dataID, token: (previous?.token ?? 0) + 1 }));
    }, [whereRequest]);

    const treeStateForComponent = useMemo(() => {
        const sourceNodes = filter ? filteredData : treeState.nodes;

        // NELL'ELENCO LA MARCA È LA SPECIE, MAI LA FOTOGRAFIA (utente, 2026-08-26). Una colonna di
        // sfere fotografate è una colonna in cui ogni riga ha un aspetto diverso e nessuna ha una
        // marca: il rosa dice «materiale» a colpo d'occhio, prima ancora che si legga il nome. La
        // fotografia è del singolo, e si guarda dove il singolo si guarda — nel suo pannello, e
        // nella vista a tessere, che è fatta per le facce ed è l'unica che le chiede.
        // LA COPIA DEVE PORTARSI IL PROPRIO PANNELLO, e non quello che il nodo aveva addosso
        // quando la copia è stata fatta. Questo giro avviene mentre si disegna, l'attacco sui nodi
        // veri in un effetto: cioè DOPO. La copia si portava quindi la chiusura di un giro prima —
        // e il pannello restava indietro di una modifica, fino a che non si cliccava altrove e si
        // tornava indietro, che è il gesto che fa ricalcolare tutto.
        const withPreviews = (nodes: MaterialNode[]): MaterialNode[] =>
            nodes.map((node) => {
                const copy: MaterialNode & { component?: () => React.ReactNode } = {
                    ...node,
                    customIcon: node.type === 'material' ? <PropertiesPanelSwatch $tint={panel.kindAsset} $size={18} /> : node.customIcon,
                    children: node.children ? withPreviews(node.children as MaterialNode[]) : node.children
                };

                copy.component = createMaterialOverlay(copy);

                return copy;
            });

        const stateForComponent = {
            ...treeState,
            nodes: withPreviews(sourceNodes as MaterialNode[])
        };

        // Applica anche il customCanTurnIntoFolder al treeStateForComponent
        stateForComponent.canTurnIntoFolder = customCanTurnIntoFolder;

        return stateForComponent;
    }, [treeState, filteredData, filter, customCanTurnIntoFolder, createMaterialOverlay]);

    // Sostituisci il canTurnIntoFolder del treeState con il nostro custom
    useEffect(() => {
        if (treeState.canTurnIntoFolder !== customCanTurnIntoFolder) {
            treeState.canTurnIntoFolder = customCanTurnIntoFolder;
        }
    }, [treeState, customCanTurnIntoFolder]);

    // Aggiorna il ref con la funzione updateNode una volta disponibile
    // tiene l'aggiornamento del name attivo sia lato overlay che tree
    useEffect(() => {
        updateNodeRef.current = updateNode;
    }, [updateNode]);

    useEffect(() => {
        if (!sceneEditor) return;

        setLocalMaterials(sceneEditor.getAllMaterials());
    }, [materialRefresh, sceneEditor]);

    // STESSA RAGIONE DELLE LUCI: i materiali possono cambiare da fuori — il modello che finisce di
    // caricarsi, il DAM che ne aggiunge uno — e questo elenco si costruisce una volta sola. Finché
    // ogni modifica della scena rimontava la sezione da capo, rinascere era il modo con cui si
    // rileggeva tutto; ora la sezione sopravvive e il segnale che arriva da fuori va ascoltato.
    useEffect(() => {
        refreshMaterials();
    }, [materials, refreshMaterials]);

    // Callback per aggiornare i materiali nel SceneEditor quando il tree cambia
    const handleTreeDataChanged = useCallback(
        (updatedNodes: MaterialNode[]) => {
            if (onMaterialsUpdated) {
                const sceneMaterials = convertToSceneMaterials(updatedNodes);
                onMaterialsUpdated(sceneMaterials);
            }
            if (sceneEditor && sceneEditor.updateMaterials) {
                sceneEditor.updateMaterials(updatedNodes);
            }
        },
        [convertToSceneMaterials, onMaterialsUpdated, sceneEditor]
    );

    // Struttura dati standard per nuovi materiali usando SceneEditor
    const createNewMaterial = useCallback(
        (name: string): MaterialNode => {
            const materialId = `material-${Date.now()}`;

            // Usa SceneEditor per creare un nuovo materiale PBR
            let newMaterial: BABYLON.Material | null = null;
            if (sceneEditor && sceneEditor.createMaterial) {
                newMaterial = sceneEditor.createMaterial(name, 'PBR');
            }

            // Se la creazione è fallita, crea un materiale mock come fallback
            if (!newMaterial) {
                newMaterial = {
                    id: materialId,
                    name,
                    materialType: 'pbr',
                    diffuseColor: '#FFFFFF',
                    specularColor: '#FFFFFF',
                    emissiveColor: '#000000',
                    roughness: 0.5,
                    metallic: 0.0,
                    enabled: true,
                    visible: true,
                    metadata: { displayOrder: nodes.length } // Il nuovo materiale va alla fine
                } as any;
            } else {
                // Assicurati che abbia metadata
                if (!newMaterial.metadata) {
                    newMaterial.metadata = {};
                }
                // Assegna displayOrder alla fine
                newMaterial.metadata.displayOrder = nodes.length;
            }

            return {
                id: newMaterial?.id || materialId,
                name: newMaterial?.name || name,
                type: 'material' as const,
                children: [],
                visible: true, // I nuovi materiali sono sempre visibili
                data: {
                    // Salva il materiale reale o mock
                    material: newMaterial,
                    // Mantieni anche le proprietà scomposte per compatibilità
                    variantId: 1,
                    materialType: 'pbr',
                    diffuseColor: '#FFFFFF',
                    specularColor: '#FFFFFF',
                    emissiveColor: '#000000',
                    roughness: 0.5,
                    metallic: 0.0,
                    enabled: true,
                    visible: true
                }
            };
        },
        [sceneEditor, nodes]
    );

    // Funzione per creare un nuovo gruppo
    const createNewGroup = useCallback((groupName: string): MaterialNode => {
        const groupId = `group-${Date.now()}`;

        return {
            id: groupId,
            name: groupName,
            type: 'group' as const,
            children: [],
            customIcon: <FolderIcon style={{ width: '16px', height: '16px', fill: panel.kindGroupInk }} />, // Icona di cartella/gruppo
            visible: true, // I gruppi sono sempre visibili
            data: {
                groupName: groupName,
                materialIds: []
            }
        };
    }, []);

    // AGGIUNGERE NON CHIEDE IL NOME PRIMA. Un modulo con un campo solo fa promettere il nome di una
    // cosa che ancora non esiste, e obbliga a deciderlo nel momento in cui se ne sa di meno. La cosa
    // nasce invece con un nome libero, diventa subito il soggetto, e il nome si aggiusta dove si
    // legge — sulla riga, con lo stesso gesto della rinomina.
    //
    // È ciò che fanno già le luci, ed è ciò che dice il mockup del gruppo: «nasce col nome
    // provvisorio e col fuoco nel campo del nome, che è l'unica cosa che gli manca».
    const bornSubject = useBornSubject(nodes, (born) => {
        treeState.setSelectedIds(new Set([born.id]));
        treeState.showOverlay(born.id);

        // Nella vista a tessere il nome non sta su una riga: lì la cosa nasce scelta, e basta.
        innerTreeRef.current?.startRename?.(born.id);
    });

    const handleNewMaterial = () => {
        closeInspector();

        const name = freeName(nodes, T._('New material', 'Admin'));
        const newMaterial = createNewMaterial(name);

        treeState.addNode(newMaterial);
        prepareMaterials();
        generatePreviews([newMaterial.data?.material]);

        bornSubject(name);
    };

    // Funzione per aggiornare i materiali dopo l'import (como prepareModel in objectEditor)
    const prepareMaterials = useCallback(() => {
        // Aggiorna i materiali nel MaterialsEditor dopo l'import (come objectEditor)
        if (sceneEditor) {
            refreshMaterials();

            const updatedMaterials = sceneEditor.getAllMaterials();

            // Notifica il componente padre con i materiali aggiornati
            if (onMaterialsUpdated) {
                onMaterialsUpdated(updatedMaterials);
            }
        }
    }, [sceneEditor, onMaterialsUpdated]);

    const handleImport = () => {
        // Verifica se la feature MaterialLibrary è disponibile
        if (!additionalFeatures?.materialLibrary) {
            showDialog(
                'Upgrade_Plan',
                <UpgradeBanner
                    marketPlace={sellerMarketplace ?? undefined}
                    message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Material Library')}
                    onUpgradeCompleted={async () => {
                        updateSubscription();
                    }}
                />
            );
            return;
        }

        closeInspector();

        // Chi c'era prima dell'import. I materiali scelti nel dialogo sono quelli della
        // libreria, non quelli che nasceranno nella scena: l'unico modo onesto di sapere
        // quali sono i nuovi è confrontare il prima e il dopo.
        const idsBeforeImport = new Set<string>((sceneEditor?.getAllMaterials?.() || []).map((material: any) => material?.id));

        showDialog(
            'import-materials',
            <MaterialImportDialog
                sceneEditor={sceneEditor}
                onMaterialsUpdated={onMaterialsUpdated}
                onImport={async (selectedMaterials) => {
                    // Mostra notifica di import in corso
                    const importNotificationId = showInfoNotification({
                        message: T._('Importing materials...', 'Admin')
                    });

                    try {
                        // Attendiamo che SceneEditor completi l'import interno
                        await new Promise((resolve) => setTimeout(resolve, 100));
                        prepareMaterials();

                        // Rimuovi notifica di progresso e mostra successo
                        removeNotification(importNotificationId);
                        showSuccessNotification({
                            message: T._(`Successfully imported ${selectedMaterials.length} material(s)`, 'Admin')
                        });

                        const imported = (sceneEditor?.getAllMaterials?.() || []).filter((material: any) => material?.id && !idsBeforeImport.has(material.id));
                        runPreviewGeneration(imported);
                    } catch (error) {
                        // Rimuovi notifica di progresso e mostra errore
                        removeNotification(importNotificationId);
                        showErrorNotification({
                            message: T._('Error importing materials: ' + (error as Error).message, 'Admin')
                        });
                        console.error('❌ Error preparing materials after import:', error);
                    }

                    // Chiudi il dialog dopo aver completato l'import
                    closeDialog('import-materials');
                }}
                onDelete={async (selectedMaterials) => {
                    // Mostra notifica di delete in corso
                    const deleteNotificationId = showInfoNotification({
                        message: T._('Deleting materials...', 'Admin')
                    });

                    try {
                        // La logica di delete è già gestita nel MaterialImportDialog
                        // Qui gestiamo solo le notifiche finali

                        // Rimuovi notifica di progresso e mostra successo
                        removeNotification(deleteNotificationId);
                        showSuccessNotification({
                            message: T._(`Successfully deleted ${selectedMaterials.length} material(s)`, 'Admin')
                        });
                    } catch (error) {
                        // Rimuovi notifica di progresso e mostra errore
                        removeNotification(deleteNotificationId);
                        showErrorNotification({
                            message: T._('Error deleting materials: ' + (error as Error).message, 'Admin')
                        });
                        console.error('❌ Error deleting materials:', error);
                    }
                }}
            />
        );
    };

    // NELLA BARRA RESTA CIÒ CHE NON HA UN SOGGETTO: «Add material» ne fa nascere uno, «Import»
    // porta roba dentro, «Generate previews» lavora su ciò che manca in tutta la libreria. Gli
    // altri comandi sono scesi nel «…» della riga, dove il soggetto è la riga stessa.
    // Una sola conferma per togliere, in tutte le liste: il testo lo compone lei.
    const askRemove = useRemovalPrompt();

    // I SOLI MATERIALI: in questa lista si prendono anche le cartelle, e una cartella dentro un'altra
    // non è una cosa che questo elenco sappia fare.
    const materialSelection = selectedIds.filter((id) => findNodeById(id)?.type === 'material');

    // SCIOGLIERE UN GRUPPO NON CANCELLA NIENTE: esce la cartella, i materiali restano nella
    // libreria. È il comando del mockup — «Remove group» — e il messaggio dice la metà che un rosso
    // non lascia indovinare, cioè che cosa resta.
    //
    // UN MOTORE SOLO, DUE PORTE: la scheda del gruppo e il «…» della sua riga chiamano questo. Due
    // porte per la stessa stanza si riconoscono dal nome; se una cancellasse e l'altra no, sarebbero
    // due comandi da imparare invece di uno.
    const dissolveGroup = (group: MaterialNode) => {
        const materials = (group.children ?? []) as MaterialNode[];

        askRemove({
            names: [group.name],
            one: T._('group', 'Admin'),
            many: T._('groups', 'Admin'),
            consequence: materials.length
                ? materials.length === 1
                    ? T._('1 material stays in the library.', 'Admin')
                    : stringFormat(T._('{0} materials stay in the library.', 'Admin'), materials.length.toString())
                : undefined,
            onConfirm: () => {
                closeInspector();

                materials.forEach((child) => {
                    const materialId = child.data?.material?._internalId || child.data?.material?.id;
                    if (!materialId) return;

                    // Lo stesso giro del trascinamento fuori da un gruppo: il motore non ha un
                    // «togli dal gruppo», ha un gruppo di riserva in cui stanno i materiali sciolti.
                    setManuallyUngroupedMaterials((prev) => new Set(prev.add(materialId)));
                    sceneEditor?.setMaterialsGroup?.([materialId], '_dummy_', true);
                });

                prepareMaterials();
            }
        });
    };

    const renameGroup = (group: MaterialNode, value: string) => {
        const newName = value.trim();
        if (!newName || newName === group.name) return;

        group.name = newName;
        if (group.data.materialIds && sceneEditor && sceneEditor.setMaterialsGroup) {
            sceneEditor.setMaterialsGroup(group.data.materialIds, newName, true);
        }

        handleTreeDataChanged(nodes);
        prepareMaterials();
    };

    // UN GRUPPO NASCE INTORNO A QUALCOSA, e non è una scelta di disegno: nel modello del motore il
    // gruppo È un nome scritto su ogni materiale — `setMaterialsGroup(ids, nome)` — quindi una cartella
    // senza materiali dentro non ha dove esistere, e all'aggiornamento successivo sparirebbe da sola.
    // È anche ciò che fa il mockup: «New group — a folder with it inside». Per questo il comando parte
    // sempre da una scelta, e per questo vive in due posti — il «…» di una riga e la barra, che agisce
    // sul mucchio preso — ma il motore è uno solo, ed è questo.
    const createGroupFrom = (ids: string[]) => {
        if (!ids.length) return;

        closeInspector();

        const groupName = freeName(nodes, T._('New group', 'Admin'));

        // Raccoglie gli ID dei materiali selezionati
        const selectedMaterialIds: string[] = [];
        const selectedNodes: MaterialNode[] = [];

        ids.forEach((id) => {
            const node = findNodeById(id);
            if (node && node.type === 'material') {
                selectedMaterialIds.push(node.data.material?.id || node.id);
                selectedNodes.push(node);
            }
        });

        if (!selectedMaterialIds.length) return;

        // Usa SceneEditor per creare il gruppo logico
        if (sceneEditor && sceneEditor.setMaterialsGroup) {
            sceneEditor.setMaterialsGroup(selectedMaterialIds, groupName, true);
        }

        // Il gruppo prende il posto in elenco del primo dei suoi
        if (selectedNodes.length > 0) {
            const sortedMaterials = selectedNodes
                .filter((node) => node.data.material && node.data.material.metadata)
                .sort((a, b) => (a.data.material.metadata?.displayOrder || 0) - (b.data.material.metadata?.displayOrder || 0));

            const groupDisplayOrder = sortedMaterials.length > 0 ? sortedMaterials[0].data.material.metadata?.displayOrder || 0 : 0;

            setGroupMaterialsDisplayOrder((prev: Map<string, number>) => new Map(prev.set(groupName, groupDisplayOrder)));

            selectedNodes.forEach((node, index) => {
                if (node.data.material) {
                    if (!node.data.material.metadata) {
                        node.data.material.metadata = {};
                    }
                    node.data.material.metadata.displayOrder = index;
                }
            });
        }

        const newGroup = createNewGroup(groupName);
        newGroup.data.materialIds = selectedMaterialIds;

        groupNodes(
            ids,
            groupName,
            () => newGroup,
            (nodeId: string) => treeState.findParentId(nodeId)
        );
        handleTreeDataChanged(nodes);
        prepareMaterials();

        bornSubject(groupName);
    };

    // UN NOME SOLO PER I DUE POSTI: la barra ripesca il comando dal menu di riga confrontandolo
    // per nome, e due stringhe uguali scritte due volte sono una che un giorno resta indietro.
    const saveToLibraryLabel = T._('Save to library', 'Admin');

    const selectableIds: string[] = (() => {
        const walk = (list: MaterialNode[]): string[] =>
            list.flatMap((node) => (node.type === 'group' ? walk((node.children ?? []) as MaterialNode[]) : [node.id]));

        return walk((filter ? filteredData : nodes) as MaterialNode[]);
    })();
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

    const tools: SceneEditorAction[] = [
        {
            name: T._('Add material', 'Admin'),
            tooltip: T._('A new material in this scene', 'Admin'),
            icon: <NewIcon />,
            kind: 'add',
            onClick: handleNewMaterial
        },
        {
            // IN BARRA E NON SOLO NEL «…» DI UNA RIGA (utente, 2026-08-26). Il mockup ce l'ha soltanto
            // sulla riga, e finché i materiali sono sei quel posto si trova; con quaranta, e con un
            // mucchio già preso, il comando che agisce su TUTTI quelli scelti va cercato dove si
            // guardano tutti — cioè qui. È lo stesso comando, non un secondo: vedi `createGroupFrom`.
            name: T._('New group', 'Admin'),
            tooltip: materialSelection.length ? T._('A folder with the chosen materials inside', 'Admin') : T._('Choose one or more materials first', 'Admin'),
            icon: <GroupIcon />,
            kind: 'action',
            disabled: !materialSelection.length,
            onClick: () => createGroupFrom(materialSelection)
        },
        {
            name: T._('Import', 'Admin'),
            tooltip: T._('From the material library', 'Admin'),
            icon: <ImportIcon />,
            kind: 'action',
            onClick: handleImport
        },
        {
            // IL VERSO OPPOSTO DI «IMPORT», e sta accanto a lui: uno prende dalla libreria
            // condivisa, l'altro ci mette dentro. Erano lontani — questo solo nel «…» di una
            // riga — e con due glifi che si somigliavano il pulsante che c'era sembrava già
            // quello che mancava. È lo STESSO comando del menu di riga, con lo stesso soggetto:
            // i materiali presi.
            name: T._('Save to library', 'Admin'),
            tooltip: materialSelection.length ? T._('Into the material library', 'Admin') : T._('Choose one or more materials first', 'Admin'),
            icon: <SaveIcon />,
            kind: 'action',
            disabled: !materialSelection.length,
            onClick: () => {
                // Si prende il comando dal menu di riga invece di riscriverne il corpo: qui dentro
                // ci sono il controllo del piano, il token e la finestra del nome, e averne due copie
                // vorrebbe dire tenerle uguali per sempre. Si cerca al momento del clic, non ora:
                // `subjectCommands` nasce più in basso.
                subjectCommands(materialSelection)
                    .find((command) => command.name === saveToLibraryLabel)
                    ?.onClick();
            }
        },
        {
            // SOLO LE MANCANTI. Rifarle tutte per averne tre significherebbe rifotografare
            // centinaia di materiali già a posto, e ogni scatto costa un ciclo di
            // smonta-e-rimonta dell'environment. Chi vuole riscattare un'anteprima che c'è ma
            // non somiglia più al materiale ha il comando dentro il materiale, ed è una scelta
            // deliberata su una cosa sola.
            name: T._('Generate previews', 'Admin'),
            tooltip: progress
                ? stringFormat(T._('Generating previews: {0} of {1}', 'Admin'), progress.done.toString(), progress.total.toString())
                : T._('Generate missing previews', 'Admin'),
            icon: <PreviewIcon />,
            kind: 'action',
            disabled: isGenerating || missing.length === 0,
            onClick: () => runPreviewGeneration(missing)
        },
        {
            // IL TASTO RESTA ACCANTO ALLA SCORCIATOIA (call coi colleghi, 2026-09-09).
            //
            // PRENDE I MATERIALI, NON I GRUPPI: un gruppo è un contenitore, e prenderlo insieme a
            // ciò che contiene metterebbe due specie nello stesso mucchio. Vale per tutte e due le
            // viste, perché guarda l'elenco e non come lo si sta guardando.
            name: allSelected ? T._('Deselect all', 'Admin') : T._('Select all', 'Admin'),
            tooltip: T._('All the materials in this list', 'Admin'),
            icon: <SelectAllIcon />,
            kind: 'action',
            active: allSelected,
            disabled: selectableIds.length === 0,
            onClick: () => setSelectedIds(allSelected ? [] : selectableIds)
        }
    ];

    // UN MOTORE, DUE PORTE — come nelle altre liste: il «…» di una riga passa quella riga, il
    // pannello del mucchio passa le righe prese. Chi chiama dice il soggetto; qui dentro la
    // selezione non si guarda mai, perché il menu di una riga non selezionata deve cadere su di
    // lei e non altrove.
    //
    // «RENAME» NON C'È PIÙ, e il nome di un materiale si cambia in un posto solo: la testata
    // dell'Inspector, come per ogni altro soggetto di questo editor. Era rimasto qui finché
    // rinominava anche i gruppi, che una scheda propria non avevano; da quando ce l'hanno (§ 4)
    // era una seconda porta per la stessa stanza.
    const subjectCommands = (ids: string[]): SceneEditorAction[] => {
        if (!ids.length) return [];

        // UN GRUPPO NON SI DUPLICA NÉ SI SALVA IN LIBRERIA: è una cartella, non un materiale, e
        // annidarne uno dentro l'altro non è una cosa che questa lista sappia fare. Gli resta il
        // toglierlo.
        const holdsGroup = ids.some((id) => findNodeById(id)?.type === 'group');

        // UN GRUPPO HA UN COMANDO SOLO, ed è toglierlo (mockup, § 4). Il nome adesso si cambia nella
        // sua scheda — dove c'è anche l'elenco di ciò che contiene — quindi «Rename» esce da qui: un
        // comando in due posti è un comando da cercare in due posti.
        const onlyGroup = ids.length === 1 ? findNodeById(ids[0]) : null;
        if (onlyGroup?.type === 'group') {
            return [
                {
                    name: T._('Remove group', 'Admin'),
                    tooltip: T._('The folder goes, the materials stay', 'Admin'),
                    icon: <RemoveIcon />,
                    kind: 'danger',
                    onClick: () => dissolveGroup(onlyGroup)
                }
            ];
        }

        const commands: SceneEditorAction[] = holdsGroup
            ? []
            : [
                  {
                      name: T._('New group', 'Admin'),
                      tooltip: T._('A folder with it inside', 'Admin'),
                      icon: <GroupIcon />,
                      kind: 'action',
                      onClick: () => createGroupFrom(ids)
                  },
                  {
                      name: T._('Duplicate', 'Admin'),
                      tooltip: T._('A copy in this scene', 'Admin'),
                      icon: <CloneIcon />,
                      kind: 'action',
                      onClick: () => {
                          if (!ids.length) return;
                          closeInspector();

                          // Clona ogni materiale selezionato usando SceneEditor
                          const newMaterialNodes: MaterialNode[] = [];
                          // I materiali Babylon nati da questa clonazione, gruppi compresi: sono loro
                          // che vanno fotografati alla fine, non i nodi dell'albero.
                          const createdMaterials: any[] = [];

                          ids.forEach((selectedId) => {
                              const selectedNode = findNodeById(selectedId);
                              if (selectedNode) {
                                  if (selectedNode.type === 'material' && selectedNode.data.material) {
                                      const originalMaterial = selectedNode.data.material;
                                      const clonedName = `${originalMaterial.name}-copy`;

                                      // Usa SceneEditor per clonare il materiale
                                      let clonedMaterial: BABYLON.Material | null = null;
                                      if (sceneEditor && sceneEditor.cloneMaterial && originalMaterial.id) {
                                          clonedMaterial = sceneEditor.cloneMaterial(originalMaterial.id, undefined, clonedName);
                                      }

                                      // Se la clonazione è riuscita, aggiungi al tree
                                      if (clonedMaterial) {
                                          // Assicurati che il materiale clonato abbia metadata
                                          if (!clonedMaterial.metadata) {
                                              clonedMaterial.metadata = {};
                                          }
                                          // Il materiale clonato va alla fine
                                          clonedMaterial.metadata.displayOrder = nodes.length + newMaterialNodes.length;

                                          const clonedNode: MaterialNode = {
                                              id: clonedMaterial.id,
                                              name: clonedMaterial.name || clonedName,
                                              type: 'material',
                                              children: [],
                                              data: {
                                                  material: clonedMaterial,
                                                  variantId: originalMaterial.variantId || 1,
                                                  materialType: originalMaterial.materialType || 'pbr',
                                                  diffuseColor: originalMaterial.diffuseColor || '#FFFFFF',
                                                  specularColor: originalMaterial.specularColor || '#FFFFFF',
                                                  emissiveColor: originalMaterial.emissiveColor || '#000000',
                                                  roughness: originalMaterial.roughness ?? 0.5,
                                                  metallic: originalMaterial.metallic ?? 0.0,
                                                  enabled: originalMaterial.enabled ?? true,
                                                  visible: originalMaterial.visible ?? true
                                              }
                                          };

                                          treeState.addNode(clonedNode);
                                          newMaterialNodes.push(clonedNode);
                                          createdMaterials.push(clonedMaterial);
                                      }
                                  } else if (selectedNode.type === 'group') {
                                      // Per i gruppi, crea una copia del gruppo
                                      const clonedGroup = createNewGroup(`${selectedNode.name}-copy`);

                                      // Clona tutti i materiali figli del gruppo
                                      if (selectedNode.children) {
                                          (selectedNode.children as MaterialNode[]).forEach((child: MaterialNode) => {
                                              if (child.type === 'material' && child.data.material) {
                                                  const originalMaterial = child.data.material;
                                                  const clonedName = `${originalMaterial.name}-copy`;

                                                  let clonedMaterial: BABYLON.Material | null = null;
                                                  if (sceneEditor && sceneEditor.cloneMaterial && originalMaterial.id) {
                                                      clonedMaterial = sceneEditor.cloneMaterial(originalMaterial.id, undefined, clonedName);
                                                  }

                                                  if (clonedMaterial) {
                                                      // Assicurati che il materiale clonato abbia metadata
                                                      if (!clonedMaterial.metadata) {
                                                          clonedMaterial.metadata = {};
                                                      }
                                                      // Per i materiali clonati nei gruppi, mantieni l'ordine originale
                                                      clonedMaterial.metadata.displayOrder = child.data.material.metadata?.displayOrder ?? 0;

                                                      const clonedChildNode: MaterialNode = {
                                                          id: clonedMaterial.id,
                                                          name: clonedMaterial.name || clonedName,
                                                          type: 'material',
                                                          children: [],
                                                          data: {
                                                              material: clonedMaterial,
                                                              variantId: originalMaterial.variantId || 1,
                                                              materialType: originalMaterial.materialType || 'pbr',
                                                              diffuseColor: originalMaterial.diffuseColor || '#FFFFFF',
                                                              specularColor: originalMaterial.specularColor || '#FFFFFF',
                                                              emissiveColor: originalMaterial.emissiveColor || '#000000',
                                                              roughness: originalMaterial.roughness ?? 0.5,
                                                              metallic: originalMaterial.metallic ?? 0.0,
                                                              enabled: originalMaterial.enabled ?? true,
                                                              visible: originalMaterial.visible ?? true
                                                          }
                                                      };
                                                      clonedGroup.children!.push(clonedChildNode);
                                                      createdMaterials.push(clonedMaterial);
                                                  }
                                              }
                                          });
                                      }

                                      treeState.addNode(clonedGroup);
                                  }
                              }
                          });

                          // USA IL PATTERN DI OBJECTEDITOR: prepareMaterials()
                          prepareMaterials();

                          generatePreviews(createdMaterials);
                      }
                  },
                  {
                      name: saveToLibraryLabel,
                      tooltip: T._('Into the material library', 'Admin'),
                      icon: <SaveIcon />,
                      kind: 'action',
                      onClick: async () => {
                          // Check della feature additionalFeatures.materialLibrary
                          if (!additionalFeatures?.materialLibrary) {
                              showDialog(
                                  'Upgrade_Plan',
                                  <UpgradeBanner
                                      marketPlace={sellerMarketplace ?? undefined}
                                      message={stringFormat(
                                          T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'),
                                          'Material Library'
                                      )}
                                      onUpgradeCompleted={async () => {
                                          updateSubscription();
                                      }}
                                  />
                              );
                              return;
                          }

                          // Chiude eventuali overlay aperti
                          closeInspector();

                          // Imposta il token globale dal cookie zakeke_token
                          const getCookieValue = (name: string): string | null => {
                              const value = `; ${document.cookie}`;
                              const parts = value.split(`; ${name}=`);
                              if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
                              return null;
                          };

                          const zakekeToken = getCookieValue('zakeke_token');
                          if (zakekeToken) {
                              // Imposta il token globalmente per le chiamate API della 3D core
                              (window as any).zakekeToken = zakekeToken;

                              // Imposta il token per jQuery AJAX globalmente
                              if ((window as any).$ && (window as any).$.ajaxSetup) {
                                  (window as any).$.ajaxSetup({
                                      beforeSend: function (xhr: any) {
                                          xhr.setRequestHeader('Authorization', `Bearer ${zakekeToken}`);
                                      }
                                  });
                              }

                              // Imposta il token per fetch globalmente
                              const originalFetch = window.fetch;
                              window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
                                  const modifiedInit: RequestInit = {
                                      ...init,
                                      headers: {
                                          ...init?.headers,
                                          Authorization: `Bearer ${zakekeToken}`
                                      }
                                  };
                                  return originalFetch.call(this, input, modifiedInit);
                              };

                              // Imposta anche come header authorization se necessario per Zakeke
                              if ((window as any).Zakeke && (window as any).Zakeke.setAuthToken) {
                                  (window as any).Zakeke.setAuthToken(zakekeToken);
                              }
                          } else {
                              console.warn('⚠️ No zakeke_token found in cookies');
                          }

                          // Funzione saveMaterials che usa makeLibraryMaterial della 3D core
                          const saveMaterials = async (materialName?: string) => {
                              const selectedMaterials = ids
                                  .map((selectedId) => findNodeById(selectedId))
                                  .filter((node) => node && node.type === 'material' && node.data.material)
                                  .map((node) => node!.data.material);

                              if (selectedMaterials.length === 1 && !materialName?.trim()) {
                                  showErrorNotification({
                                      message: T._('Please enter a valid material name', 'Admin')
                                  });
                                  return;
                              }

                              // Mostra notifica di inizio salvataggio
                              const saveNotificationId = showInfoNotification({
                                  message: T._('Saving materials to library...', 'Admin')
                              });

                              try {
                                  let materialCount = 0;
                                  for (const material of selectedMaterials) {
                                      materialCount++;

                                      // Usa sceneEditor.makeLibraryMaterial come nel vecchio SceneEditorPage
                                      if (sceneEditor && sceneEditor.makeLibraryMaterial) {
                                          const materialName_final = materialName || material.name || `Material ${materialCount}`;

                                          try {
                                              await sceneEditor.makeLibraryMaterial(material.id, materialName_final);
                                          } catch (materialError) {
                                              console.error(
                                                  `❌ Error saving material ${materialName_final} via sceneEditor.makeLibraryMaterial:`,
                                                  materialError
                                              );
                                              throw materialError;
                                          }
                                      } else {
                                          const error = 'sceneEditor.makeLibraryMaterial method not available';
                                          console.error(`❌ ${error}`);
                                          throw new Error(error);
                                      }
                                  }

                                  // Rimuovi notifica di progresso e mostra successo
                                  removeNotification(saveNotificationId);
                                  showSuccessNotification({
                                      message: T._(`Successfully saved ${selectedMaterials.length} material(s) to library`, 'Admin')
                                  });
                              } catch (error) {
                                  // Rimuovi notifica di progresso e mostra errore
                                  removeNotification(saveNotificationId);
                                  showErrorNotification({
                                      message: T._('Error saving materials to library: ' + (error as Error).message, 'Admin')
                                  });
                                  console.error('❌ Error in save process:', error);
                              }
                          };

                          // Logic di controllo materiali selezionati (come nel vecchio codice)
                          const selectedMaterials = ids.map((selectedId) => findNodeById(selectedId)).filter((node) => node && node.type === 'material');

                          if (selectedMaterials.length === 0) {
                              alert('Select at least one material to save');
                          } else if (selectedMaterials.some((node) => node!.type === 'group')) {
                              alert('Select only materials, not groups');
                          } else {
                              // Se è selezionato un solo materiale, chiedi il nome tramite dialog
                              if (selectedMaterials.length === 1) {
                                  const material = selectedMaterials[0]!.data.material;
                                  const SaveMaterialDialog = () => {
                                      const validationContext = useValidationContext();
                                      const [name, setName] = useState(material.name || '');

                                      const handleSaveMaterial = async () => {
                                          if (!validationContext.submit()) return;

                                          closeDialog('save-material-dialog');
                                          await saveMaterials(name.trim());
                                      };

                                      return (
                                          <Dialog
                                              title={T._('Save the material to the library', 'Admin')}
                                              buttons={[
                                                  {
                                                      label: T._('Save', 'Admin'),
                                                      onClick: handleSaveMaterial
                                                  }
                                              ]}
                                          >
                                              <FormControl label={T._('Name in the library', 'Admin')}>
                                                  <Input
                                                      placeholder={T._('Type the name it takes in the library...', 'Admin')}
                                                      fullWidth
                                                      value={name}
                                                      onChange={(e) => setName(e.target.value)}
                                                      validationKey='libraryMaterialName'
                                                      validationLabel={T._('Name in the library', 'Admin')}
                                                      validate={validateRequiredTrimmed}
                                                      validationMessage={T._('The name in the library is required.', 'Admin')}
                                                  />
                                              </FormControl>
                                          </Dialog>
                                      );
                                  };

                                  showDialog(
                                      'save-material-dialog',
                                      <ValidationStateProvider>
                                          <SaveMaterialDialog />
                                      </ValidationStateProvider>
                                  );
                              } else {
                                  // Se sono selezionati più materiali, usa i loro nomi esistenti
                                  await saveMaterials();
                              }
                          }
                      }
                  }
              ];

        commands.push({
            name: T._('Remove', 'Admin'),
            tooltip: T._('Out of this scene', 'Admin'),
            icon: <RemoveIcon />,
            kind: 'danger',
            onClick: () => {
                closeInspector();

                const taken = ids.map((id) => findNodeById(id)).filter((node): node is NonNullable<typeof node> => node != null);

                // GLI OGGETTI CHE LO PORTAVANO RESTANO SENZA, e chi conferma deve saperlo prima:
                // dall'elenco dei materiali non si vede chi li sta usando.
                const materialIds = taken.flatMap((node) =>
                    node.type === 'group'
                        ? ((node.children ?? []) as MaterialNode[]).map((child) => child.data?.material?.id).filter(Boolean)
                        : [node.data?.material?.id].filter(Boolean)
                );
                const orphans = (sceneEditor?.getSceneMeshes?.() ?? []).filter(
                    (mesh: any) => mesh?.material?.id && materialIds.includes(mesh.material.id)
                ).length;

                askRemove({
                    names: taken.map((node) => node.name),
                    one: T._('material', 'Admin'),
                    many: T._('materials', 'Admin'),
                    consequence: orphans
                        ? orphans === 1
                            ? T._('1 object will be left without a material.', 'Admin')
                            : stringFormat(T._('{0} objects will be left without a material.', 'Admin'), orphans.toString())
                        : undefined,
                    onConfirm: () => {
                        // Rimuovi ogni materiale selezionato usando SceneEditor
                        taken.forEach((selectedNode) => {
                            if (selectedNode.type === 'material' && selectedNode.data.material) {
                                const material = selectedNode.data.material;

                                // Usa SceneEditor per rimuovere il materiale
                                if (sceneEditor && sceneEditor.deleteMaterial && material.id) {
                                    sceneEditor.deleteMaterial(material.id);
                                }

                                // Rimuovi anche dal material group se esiste
                                if (sceneEditor && sceneEditor.removeMaterialGroup && material.id) {
                                    sceneEditor.removeMaterialGroup(material.id);
                                }
                            } else if (selectedNode.type === 'group' && selectedNode.children) {
                                // Per i gruppi, rimuovi prima tutti i materiali figli
                                (selectedNode.children as MaterialNode[]).forEach((child: MaterialNode) => {
                                    if (child.type === 'material' && child.data.material) {
                                        const material = child.data.material;
                                        if (sceneEditor && sceneEditor.deleteMaterial && material.id) {
                                            sceneEditor.deleteMaterial(material.id);
                                        }
                                        if (sceneEditor && sceneEditor.removeMaterialGroup && material.id) {
                                            sceneEditor.removeMaterialGroup(material.id);
                                        }
                                    }
                                });
                            }
                        });

                        // Rimuovi dal tree state
                        removeNodes(ids);

                        // USA IL PATTERN DI OBJECTEDITOR: prepareMaterials()
                        prepareMaterials();
                    }
                });
            }
        });

        return commands;
    };

    // I comandi sono funzioni nuove a ogni render: l'effetto qui sotto non può dipenderne o
    // riparte all'infinito (manda un elemento nuovo all'Inspector, che rende di nuovo l'editor,
    // che rifà i comandi). Guarda la selezione, e i comandi li legge da qui.
    const subjectCommandsRef = useRef(subjectCommands);
    subjectCommandsRef.current = subjectCommands;

    // Con più di un materiale preso, l'Inspector guarda il mucchio: stessa regola delle altre
    // liste, e delle due viste. ⇧ prende un intervallo, ⌘ (Ctrl) una cosa per volta: un tasto
    // non può voler dire due cose nella stessa applicazione, e impararlo due volte sarebbe un
    // difetto, non una scelta.
    useEffect(() => {
        if (!onInspectorChange || selectedIds.length < 2) return;

        const entries = selectedIds
            .map((id) => findNodeById(id))
            .filter((node): node is NonNullable<typeof node> => node != null)
            .map((node) => ({ id: node.id, name: node.name, icon: <PaletteSolidIcon /> }));

        onInspectorChange(<SelectionInspector entries={entries} actions={subjectCommandsRef.current(selectedIds)} subjectLabel={T._('materials', 'Admin')} />);
    }, [selectedIds, findNodeById, onInspectorChange]);

    // LE DUE VISTE DELLA LIBRERIA, come nel prototipo. La griglia mostra i materiali per
    // quello che sono — una superficie, non una riga di testo — e i gruppi diventano titoli
    // sopra le loro tessere. L'elenco è l'albero di sempre, ed è lì che il raggruppamento
    // si FA: si trascina un materiale sopra un altro e nasce il gruppo. Le due viste
    // leggono gli stessi nodi, quindi un gruppo creato nell'elenco è un titolo nella
    // griglia un istante dopo.
    const gridNodes = filter ? filteredData : treeState.nodes;
    // L'AVVISO STA IN BARRA, NON IN UNA DIALOG. Le anteprime mancanti sono uno stato, non un
    // evento: una finestra che chiede il permesso a ogni ingresso diventa un rumore da
    // chiudere senza leggere, mentre un badge accanto ai comandi lo dice e sta zitto. Sparisce
    // da sé quando non manca più nessuno, quindi non c'è nessuna preferenza da ricordare — è il
    // dato stesso a decidere se c'è.
    const trailing = (
        <>
            {missing.length > 0 && (
                <Badge variant='warning' title={T._('Use «Generate missing previews» in the toolbar to create them.', 'Admin')}>
                    {stringFormat(T._('{0} previews missing', 'Admin'), missing.length.toString())}
                </Badge>
            )}
            <ResourceListViewModeSwitch
                viewMode={viewMode}
                viewModeLabel={T._('Materials view', 'Admin')}
                cardsViewLabel={T._('Grid view', 'Admin')}
                tableViewLabel={T._('List view', 'Admin')}
                cardsIcon={<ViewGridIcon />}
                tableIcon={<ViewListIcon />}
                onViewModeChange={setViewMode}
            />
        </>
    );

    // IN GRIGLIA IL PANNELLO SEGUE LA SCELTA, e non è un secondo gesto accanto al primo. Non c'è
    // un albero che possieda l'overlay — in elenco è lui a rifarlo a ogni movimento — quindi qui
    // lo pubblica la sezione, guardando la selezione: una tessera sola mostra la sua scheda, il
    // mucchio lo raccoglie l'effetto sopra, e con niente preso la colonna si svuota.
    //
    // Scriverlo dentro chi clicca era il modo di pubblicare la scheda SOLO quando si clicca: con
    // ⇧ e ⌘ la selezione cambia anche in altri modi, e la colonna restava indietro.
    const overlayOfRef = useRef(createMaterialOverlay);
    overlayOfRef.current = createMaterialOverlay;

    useEffect(() => {
        if (viewMode !== 'cards' || !onInspectorChange || selectedIds.length > 1) return;

        if (!selectedIds.length) {
            onInspectorChange(null);
            return;
        }

        const node = findNodeById(selectedIds[0]);
        if (node) onInspectorChange(overlayOfRef.current(node as MaterialNode)());
    }, [viewMode, selectedIds, findNodeById, onInspectorChange]);

    const showInInspector = (node: MaterialNode) => setSelectedIds([node.id]);

    // APRIRE UN SOGGETTO È UN GESTO SOLO, e le due viste lo fanno in due modi: in elenco lo apre
    // l'albero, che è chi possiede l'overlay; in griglia lo pubblica la sezione. Chi chiama non deve
    // sapere in quale vista si trova — e infatti non lo sa.
    const openNode = (node: MaterialNode) => {
        if (viewMode === 'cards') {
            showInInspector(node);
            return;
        }

        setSelectedIds([node.id]);
        treeState.showOverlay(node.id);
    };

    groupCardRef.current = {
        open: (materialNodeId) => {
            const material = findNodeById(materialNodeId);
            if (material) openNode(material);
        },
        rename: renameGroup,
        dissolve: dissolveGroup
    };

    // «PORTAMI A QUEL MATERIALE» DEVE FUNZIONARE IN TUTT'E DUE LE VISTE.
    //
    // Il rimando che arriva dal pannello di una mesh — clicchi il nome del materiale e ti aspetti
    // di trovartelo davanti — passa da `treeRef`, che è il riferimento all'ALBERO. Ma la vista a
    // griglia un albero non lo monta affatto: il riferimento era nullo, e il rimando portava alla
    // sezione giusta senza aprire niente. Sembrava che il clic non facesse nulla.
    //
    // Il riferimento lo tiene quindi la sezione, che sa in quale vista si trova: in elenco
    // inoltra all'albero, in griglia sceglie la tessera da sé. Chi chiama non deve sapere niente
    // di tutto questo — e infatti non lo sa.
    const innerTreeRef = useRef<TreeHandle>(null);

    useImperativeHandle(
        treeRef,
        () => ({
            closeOverlay: () => {
                if (viewMode === 'cards') {
                    setSelectedIds([]);
                    onInspectorChange?.(null);
                    return;
                }
                innerTreeRef.current?.closeOverlay?.();
            },
            selectAndOpenOverlay: (dataID: string) => {
                if (viewMode !== 'cards') {
                    innerTreeRef.current?.selectAndOpenOverlay?.(dataID);
                    return;
                }

                const found = treeState.findNodeByDataID(treeState.nodes as MaterialNode[], dataID);
                if (found) showInInspector(found as MaterialNode);
            },
            // Nella vista a tessere il nome non sta su una riga: non c'è niente da aprire in scrittura.
            startRename: (nodeId: string) => innerTreeRef.current?.startRename?.(nodeId)
        }),
        [viewMode, treeState, onInspectorChange, createMaterialOverlay]
    );

    // L'ANTEPRIMA VERA SE C'È, ALTRIMENTI UN SURROGATO. Quando il materiale ha la sua foto
    // — appena scattata o già salvata nella scena — la tessera mostra quella. Quando non
    // c'è, il tondo è una sfera FINTA: un gradiente costruito sul colore base, che non sa
    // niente di riflessi, rugosità o trasparenza. È un segnaposto dichiarato.
    // LE TESSERE SI PRENDONO COME LE RIGHE. L'intervallo si conta su `order`, che è la sequenza
    // delle tessere COME STANNO A SCHERMO: i gruppi aperti coi loro dentro, poi gli sciolti.
    // L'ordine dell'albero qui mentirebbe — la griglia mette i gruppi in cima e l'albero no — e un
    // intervallo contato sul posto sbagliato prende cose che non stavano fra le due che hai toccato.
    // IL NOME SI CAMBIA DOVE SI LEGGE, e i due modi di guardare la stessa libreria — le righe e le
    // tessere — lo scrivono nello stesso posto.
    //
    // UN GRUPPO NON SI RINOMINA COME UN MATERIALE: il primo è un contenitore nostro e la sua scheda
    // sa già farlo, il secondo è un oggetto del motore e il nome è suo.
    const renameNode = (node: MaterialNode, name: string) => {
        if (node.type === 'group') {
            groupCardRef.current?.rename(node, name);
            return;
        }

        const material = node.data?.material;
        if (!material) return;

        material.name = name;
        // IL NOME STA IN DUE POSTI — sul materiale del motore e sul nodo dell'albero — e vanno
        // scritti tutti e due: il primo è quello che si salva, il secondo quello che si legge nella
        // riga e nella testata del pannello.
        updateNodeRef.current?.(node.id, { name });
    };

    const rename = useInlineRename();

    const renderCard = (node: MaterialNode, order: string[]) => {
        const preview = previewOf(node);
        const fallback = materialSwatchFallback(node.data?.diffuseColor);
        const isSelected = selectedIds.includes(node.id);
        const editing = rename.editingId === node.id;

        return (
            <AssetCard key={node.id} $selected={isSelected}>
                {/* IL CAMPO NON PUÒ STARE DENTRO UN PULSANTE: mentre si scrive il nome la superficie
                    smette di essere il comando che sceglie, e torna a esserlo appena si esce. */}
                <AssetCardSurface
                    as={editing ? 'div' : undefined}
                    type={editing ? undefined : 'button'}
                    onClick={editing ? undefined : (e) => treeState.toggleSelection(node.id, e.shiftKey, e.ctrlKey || e.metaKey, order)}
                    aria-pressed={editing ? undefined : isSelected}
                >
                    <AssetSwatch $round style={preview ? { backgroundImage: `url(${preview})` } : { background: fallback }} />
                    {editing ? (
                        <AssetNameInput aria-label={T._('Name', 'Admin')} {...rename.fieldProps(node.name, (value) => renameNode(node, value))} />
                    ) : (
                        <AssetName
                            title={node.name}
                            onClick={(event) => {
                                // IL SECONDO CLIC, NON IL DOPPIO, come sulle righe: si apre solo il nome di
                                // una tessera già scelta, e scelta da sola.
                                if (!isSelected || selectedIds.length !== 1) return;

                                event.stopPropagation();
                                rename.start(node.id, node.name);
                            }}
                        >
                            {markMatches(node.name, filter)}
                        </AssetName>
                    )}
                </AssetCardSurface>

                {/* GLI STESSI COMANDI DELLA RIGA, perché è lo stesso materiale: la vista cambia come lo
                    si guarda, non che cosa se ne può fare. Prima la griglia ne era priva, e per
                    duplicare un materiale bisognava tornare in elenco. */}
                <AssetCardActions>
                    <CommandMenu actions={subjectCommands([node.id])} />
                </AssetCardActions>
            </AssetCard>
        );
    };

    if (viewMode === 'cards') {
        const loose = gridNodes.filter((node: MaterialNode) => node.type !== 'group');
        const groups = gridNodes.filter((node: MaterialNode) => node.type === 'group');

        // Le testate dei gruppi restano fuori dal conto: un intervallo che inghiottisse una cartella
        // prenderebbe due specie insieme, e «New group» lavora sui materiali.
        const cardOrder = [
            ...groups.flatMap((group: MaterialNode) => (closedGroups.has(group.id) ? [] : ((group.children ?? []) as MaterialNode[]))),
            ...loose
        ].map((node: MaterialNode) => node.id);

        return (
            <SectionPanel
                label={T._('Materials library', 'Admin')}
                actions={tools}
                trailing={trailing}
                filter={filter}
                onFilterChange={setFilter}
                showUsageFilter
                searchPlaceholder={T._('Search materials...', 'Admin')}
            >
                {gridNodes.length === 0 && <AssetEmptyState icon={<PaletteSolidIcon />}>{T._('No materials in the scene yet.', 'Admin')}</AssetEmptyState>}

                {/* I GRUPPI STANNO IN CIMA, e gli sciolti dopo la loro fascia: un gruppo è una
                    SEZIONE della libreria, e una sezione che comincia in fondo si legge per ultima
                    anche quando è la più importante. È anche il solo ordine in cui la fascia degli
                    sciolti significa qualcosa — «da qui in giù, nessun gruppo». */}
                {groups.map((group: MaterialNode) => {
                    const materials = (group.children ?? []) as MaterialNode[];
                    const isOpen = !closedGroups.has(group.id);

                    return (
                        <React.Fragment key={group.id}>
                            <AssetGroupRow $selected={selectedIds.includes(group.id)}>
                                <TreeTwisty
                                    type='button'
                                    $isOpen={isOpen}
                                    aria-expanded={isOpen}
                                    aria-label={stringFormat(isOpen ? T._('Collapse {0}', 'Admin') : T._('Expand {0}', 'Admin'), group.name)}
                                    onClick={() => toggleGroup(group.id)}
                                />
                                <AssetGroupGlyph aria-hidden='true'>
                                    <FolderIcon />
                                </AssetGroupGlyph>
                                <AssetGroupName type='button' title={group.name} onClick={() => showInInspector(group)}>
                                    {markMatches(group.name, filter)}
                                </AssetGroupName>
                                <AssetGroupCount title={stringFormat(T._('{0} materials in this group', 'Admin'), materials.length.toString())}>
                                    {materials.length}
                                </AssetGroupCount>
                            </AssetGroupRow>

                            {isOpen && <AssetGrid>{materials.map((material) => renderCard(material, cardOrder))}</AssetGrid>}
                        </React.Fragment>
                    );
                })}

                {/* LA FASCIA C'È SOLO SE C'È ANCHE UN GRUPPO. Con tutti i materiali sciolti, «Not in a
                    group» sarebbe un titolo che divide una cosa sola dal niente. */}
                {loose.length > 0 && groups.length > 0 && <AssetGroupTitle>{T._('Not in a group', 'Admin')}</AssetGroupTitle>}
                {loose.length > 0 && <AssetGrid>{loose.map((material) => renderCard(material, cardOrder))}</AssetGrid>}
            </SectionPanel>
        );
    }

    return (
        <SectionPanel
            label={T._('Materials library', 'Admin')}
            actions={tools}
            trailing={trailing}
            filter={filter}
            onFilterChange={setFilter}
            showUsageFilter
            searchPlaceholder={T._('Search materials...', 'Admin')}
        >
            <TreeComponent
                ref={innerTreeRef}
                highlight={filter}
                treeState={treeStateForComponent}
                expandAll={!!filter}
                treeLabel='Scene materials'
                showVisibilityIcon={false}
                disableOverlay={!!onInspectorChange}
                onSelectedComponentChange={onInspectorChange}
                /* IL NOME SI CAMBIA DOVE SI LEGGE — in elenco, sulle tessere e nella testata
                   dell'Inspector: un secondo clic sul nome di una riga già scelta lo apre. Erano due
                   posti a saper rinominare — la testata e una finestra — e in libreria nessuno. */
                onNodeRename={renameNode}
                /* LA RIGA DI UN GRUPPO NON HA IL «…»: il suo unico comando — toglierlo — vive in
                   fondo alla scheda che si apre premendola, e un menu per arrivare a una voce sola
                   è un gesto in più per la stessa cosa. È la regola del mockup, ed è la stessa che
                   il Composer applica alle testate che di comandi ne hanno pochi. */
                blinkRequest={blinkRequest}
                rowActions={(node) => (node.type === 'group' ? [] : subjectCommands([node.id]))}
                /* IL NUMERO IN CODA AL NOME: quanti oggetti portano questo materiale. È la domanda
                   che si fa scorrendo una libreria — «questo lo sta usando qualcuno?» — e la sua
                   risposta stava solo dentro il pannello, cioè una riga per volta. Lo ZERO si scrive:
                   è la risposta più utile delle quattro, e un vuoto si legge come «non lo so». */
                rowNote={(node) => {
                    const id = node.data?.material?.id;
                    if (!id) return null;

                    const count = usageByMaterial.get(id) ?? 0;
                    return {
                        text: String(count),
                        title: count === 1 ? T._('Used by 1 object', 'Admin') : stringFormat(T._('Used by {0} objects', 'Admin'), String(count))
                    };
                }}
                onNodeMoved={(nodeId, targetParentId, index) => {
                    // Usa la funzione unificata che può cercare sia nei filteredData che nel treeState originale
                    const nodesToMove = treeState.selectedIds.has(nodeId) ? Array.from(treeState.selectedIds) : [nodeId];

                    // Trova il nodo target per verificare se è un gruppo
                    const targetNode = targetParentId ? findNodeByIdUnified(targetParentId) : null;

                    // Trova il nodo che viene trascinato
                    const draggedNode = findNodeByIdUnified(nodeId);

                    // INTERCETTA IMMEDIATAMENTE il drag di materiale su materiale per evitare il warning
                    if (draggedNode?.type === 'material' && targetNode?.type === 'material') {
                        // DRAG & DROP di materiali su un altro MATERIALE per RIORDINO

                        // DETERMINA SE SIAMO DENTRO UN GRUPPO O NELLA RADICE
                        const draggedParentId = treeState.findParentId(nodeId);
                        const targetParentId_actual = treeState.findParentId(targetParentId!);

                        // VERIFICA IL CONTESTO E GESTISCI I DRAG TRA CONTESTI DIVERSI
                        if (draggedParentId !== targetParentId_actual) {
                            // DRAG DA CONTESTI DIVERSI (es. da gruppo a root, o da root a gruppo)

                            // GESTISCI IL CASO: materiali da gruppo trascinati su materiale root
                            if (draggedParentId && !targetParentId_actual) {
                                // I materiali spostati devono:
                                // 1. Essere disassociati dal gruppo di origine
                                // 2. Essere assegnati al gruppo "_dummy_" (materiali root)
                                // 3. Avere displayOrder riorganizzato rispetto al target root

                                const targetMaterial = findNodeByIdUnified(targetParentId!);
                                if (!targetMaterial || targetMaterial.type !== 'material') {
                                    console.warn('❌ Target material not found');
                                    return;
                                }

                                const targetDisplayOrder = targetMaterial.data.material?.metadata?.displayOrder ?? 0;

                                // Sposta i materiali trascinati e riorganizza i displayOrder
                                nodesToMove.forEach((draggedNodeId, index) => {
                                    const draggedNode = findNodeByIdUnified(draggedNodeId);
                                    if (draggedNode && draggedNode.type === 'material') {
                                        const materialId = draggedNode.data.material?._internalId || draggedNode.data.material?.id;

                                        if (materialId && draggedNode.data.material) {
                                            // 1. TRACCIA COME MANUALMENTE DISASSOCIATO
                                            setManuallyUngroupedMaterials((prev) => new Set(prev.add(materialId)));

                                            // 2. DISASSOCIA DAL GRUPPO DI ORIGINE
                                            if (sceneEditor && sceneEditor.setMaterialsGroup) {
                                                sceneEditor.setMaterialsGroup([materialId], '_dummy_', true);
                                            }

                                            // 3. RIORGANIZZA DISPLAYORDER - inserisci dopo il target
                                            const newDisplayOrder = targetDisplayOrder + 1 + index;
                                            if (!draggedNode.data.material.metadata) {
                                                draggedNode.data.material.metadata = {};
                                            }
                                            draggedNode.data.material.metadata.displayOrder = newDisplayOrder;

                                            // 4. RIMUOVI DAL GRUPPO VISUALE
                                            const currentParentId = treeState.findParentId(draggedNodeId);
                                            if (currentParentId) {
                                                const parentGroup = findNodeById(currentParentId);
                                                if (parentGroup && parentGroup.type === 'group' && parentGroup.data.materialIds) {
                                                    parentGroup.data.materialIds = parentGroup.data.materialIds.filter((id) => id !== materialId);
                                                }
                                            }
                                        }
                                    }
                                });

                                // RIORDINA TUTTI I MATERIALI ROOT per mantenere la sequenza corretta
                                const allRootMaterials = nodes
                                    .filter((node) => node.type === 'material')
                                    .sort((a, b) => {
                                        const aOrder = a.data.material?.metadata?.displayOrder ?? 0;
                                        const bOrder = b.data.material?.metadata?.displayOrder ?? 0;
                                        return aOrder - bOrder;
                                    });

                                // SPOSTA visivamente nel tree
                                treeState.moveSelection(nodesToMove, null); // null = root level

                                // NOTIFICA IL CAMBIAMENTO
                                setTimeout(() => {
                                    if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                        onMaterialsUpdated(sceneEditor.getMaterials());
                                    }
                                }, 50);

                                return; // ESCI - drag gestito
                            }

                            // GESTISCI IL CASO: materiali root trascinati su materiale in gruppo
                            if (!draggedParentId && targetParentId_actual) {
                                const targetGroup = findNodeByIdUnified(targetParentId_actual);
                                const targetMaterial = findNodeByIdUnified(targetParentId!);

                                if (!targetGroup || targetGroup.type !== 'group' || !targetMaterial) {
                                    console.warn('❌ Target group or material not found');
                                    return;
                                }

                                // I materiali spostati devono:
                                // 1. Essere rimossi dal tracking manualmente disassociati
                                // 2. Essere assegnati al gruppo target
                                // 3. Avere internalDisplayOrder riorganizzato rispetto al target nel gruppo

                                const targetInternalOrder = targetMaterial.data.material?.metadata?.internalDisplayOrder ?? 0;

                                const materialIdsToGroup: string[] = [];

                                nodesToMove.forEach((draggedNodeId, index) => {
                                    const draggedNode = findNodeByIdUnified(draggedNodeId);
                                    if (draggedNode && draggedNode.type === 'material') {
                                        const materialId = draggedNode.data.material?._internalId || draggedNode.data.material?.id;

                                        if (materialId && draggedNode.data.material) {
                                            // 1. RIMUOVI DAL TRACKING DISASSOCIATI
                                            if (manuallyUngroupedMaterials.has(materialId)) {
                                                setManuallyUngroupedMaterials((prev) => {
                                                    const newSet = new Set(prev);
                                                    newSet.delete(materialId);
                                                    return newSet;
                                                });
                                            }

                                            // 2. AGGIUNGI AL GRUPPO TARGET
                                            materialIdsToGroup.push(materialId);

                                            // 3. ASSEGNA internalDisplayOrder - inserisci dopo il target
                                            const newInternalOrder = targetInternalOrder + 1 + index;
                                            if (!draggedNode.data.material.metadata) {
                                                draggedNode.data.material.metadata = {};
                                            }
                                            draggedNode.data.material.metadata.internalDisplayOrder = newInternalOrder;
                                        }
                                    }
                                });

                                // AGGIORNA IL GRUPPO nel SceneEditor
                                if (materialIdsToGroup.length > 0 && sceneEditor && sceneEditor.setMaterialsGroup) {
                                    const existingMaterialIds = targetGroup.data.materialIds || [];
                                    const allMaterialIds = [...new Set([...existingMaterialIds, ...materialIdsToGroup])];

                                    sceneEditor.setMaterialsGroup(allMaterialIds, targetGroup.name, true);

                                    // Aggiorna i materialIds del gruppo
                                    targetGroup.data.materialIds = allMaterialIds;
                                }

                                // SPOSTA visivamente nel tree
                                treeState.moveSelection(nodesToMove, targetParentId_actual);

                                // FORZA IL RE-RENDER
                                // NOTIFICA IL CAMBIAMENTO
                                setTimeout(() => {
                                    if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                        onMaterialsUpdated(sceneEditor.getMaterials());
                                    }
                                }, 50);

                                return; // ESCI - drag gestito
                            }

                            // Altri tipi di drag cross-context possono essere gestiti qui
                            console.warn('❌ Cross-context drag not supported for this combination');
                            return;
                        }

                        if (draggedParentId && targetParentId_actual) {
                            // **RIORDINO INTERNO AL GRUPPO** - APPROCCIO SEMPLIFICATO

                            const parentGroup = findNodeByIdUnified(draggedParentId);
                            if (!parentGroup || parentGroup.type !== 'group') {
                                console.warn('❌ Parent group not found');
                                return;
                            }

                            // Ottieni tutti i materiali del gruppo
                            const groupMaterials = ((parentGroup.children as MaterialNode[]) || []).filter((child) => child.type === 'material');

                            const materialsBeingMoved = groupMaterials.filter((m) => nodesToMove.includes(m.id));
                            const targetMaterial = groupMaterials.find((m) => m.id === targetParentId);

                            if (materialsBeingMoved.length === 0 || !targetMaterial) {
                                console.warn('❌ Could not find moved or target materials in group');
                                return;
                            }

                            // Rimuovi i materiali trascinati dalla lista
                            const materialsWithoutMoved = groupMaterials.filter((m) => !nodesToMove.includes(m.id));

                            // Trova dove inserire (dopo il target)
                            const targetIndex = materialsWithoutMoved.findIndex((m) => m.id === targetParentId);
                            const insertIndex = targetIndex + 1;

                            // Crea il nuovo ordine
                            const reorderedMaterials = [
                                ...materialsWithoutMoved.slice(0, insertIndex),
                                ...materialsBeingMoved,
                                ...materialsWithoutMoved.slice(insertIndex)
                            ];

                            // Aggiorna SOLO i displayOrder interni
                            reorderedMaterials.forEach((material, index) => {
                                if (material.data.material) {
                                    if (!material.data.material.metadata) material.data.material.metadata = {};
                                    material.data.material.metadata.internalDisplayOrder = index;
                                }
                            });

                            // Aggiorna SOLO i children del gruppo
                            parentGroup.children = reorderedMaterials;

                            // Forza re-render con updateNode invece di setNodes
                            updateNode(parentGroup.id, { children: reorderedMaterials });

                            // Notifica SceneEditor
                            setTimeout(() => {
                                if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                    onMaterialsUpdated(sceneEditor.getMaterials());
                                }
                            }, 0);

                            return; // Esci - tutto gestito
                        } else {
                            // **RIORDINO NELLA RADICE (livello globale)**

                            // OTTIMIZZAZIONE: Riordina TUTTI i materiali selezionati basandosi sulla nuova posizione
                            const allMaterialNodes = nodes.filter((node) => node.type === 'material');
                            const materialsBeingMoved = allMaterialNodes.filter((n) => nodesToMove.includes(n.id));
                            const targetMaterialNode = allMaterialNodes.find((n) => n.id === targetParentId);

                            if (materialsBeingMoved.length === 0 || !targetMaterialNode) {
                                console.warn('❌ Could not find moved or target material nodes');
                                return;
                            }

                            // Rimuovi TUTTI i materiali selezionati dalla loro posizione attuale
                            const materialsWithoutMoved = allMaterialNodes.filter((n) => !nodesToMove.includes(n.id));

                            // Trova l'indice dove inserire i materiali (dopo il target)
                            const targetIndex = materialsWithoutMoved.findIndex((n) => n.id === targetParentId);
                            const insertIndex = targetIndex + 1;

                            // Inserisci TUTTI i materiali selezionati nella nuova posizione
                            const reorderedMaterials = [
                                ...materialsWithoutMoved.slice(0, insertIndex),
                                ...materialsBeingMoved,
                                ...materialsWithoutMoved.slice(insertIndex)
                            ];

                            // RIASSEGNA DISPLAYORDER SEQUENZIALI PER EVITARE DUPLICATI
                            reorderedMaterials.forEach((material, index) => {
                                if (material.data.material) {
                                    // Assicurati che abbia metadata
                                    if (!material.data.material.metadata) {
                                        material.data.material.metadata = {};
                                    }
                                    // Assegna displayOrder sequenziale basato sulla nuova posizione
                                    material.data.material.metadata.displayOrder = index;
                                }
                            });

                            // CREA UNA COPIA COMPLETA DEI NODI con i nuovi displayOrder
                            const nodesCopy = nodes.map((node) => {
                                if (node.type === 'material') {
                                    // Trova il materiale riordinato corrispondente
                                    const reorderedMaterial = reorderedMaterials.find((m) => m.id === node.id);
                                    if (reorderedMaterial && reorderedMaterial.data.material && node.data.material) {
                                        return {
                                            ...node,
                                            data: {
                                                ...node.data,
                                                material: {
                                                    ...node.data.material,
                                                    metadata: {
                                                        ...node.data.material.metadata,
                                                        displayOrder: reorderedMaterial.data.material.metadata?.displayOrder
                                                    }
                                                }
                                            }
                                        };
                                    }
                                }
                                return node; // Nodi gruppo rimangono invariati
                            });

                            // RIORDINA I NODI FINALI in base al nuovo displayOrder
                            const finalNodes = nodesCopy.sort((a, b) => {
                                if (a.type === 'material' && b.type === 'material' && a.data.material && b.data.material) {
                                    const aOrder = a.data.material.metadata?.displayOrder ?? 0;
                                    const bOrder = b.data.material.metadata?.displayOrder ?? 0;
                                    return aOrder - bOrder;
                                }
                                // I gruppi mantengono la loro posizione relativa
                                return 0;
                            });

                            // FORZA IL RE-RENDER COMPLETO sostituendo completamente i nodi
                            setNodes(finalNodes);
                        }

                        // NOTIFICA LO SCENE EDITOR con i materiali aggiornati
                        if (sceneEditor && sceneEditor.getMaterials) {
                            const updatedMaterials = sceneEditor.getMaterials();
                            if (onMaterialsUpdated) {
                                onMaterialsUpdated(updatedMaterials);
                            }
                        }

                        // ASSICURATI che i materiali riordinati a root rimangano nel gruppo _dummy_
                        setTimeout(() => {
                            const currentRootMaterials = nodes
                                .filter((node: MaterialNode) => node.type === 'material')
                                .map((node: MaterialNode) => node.data.material?._internalId || node.data.material?.id)
                                .filter((id: string | undefined) => id) as string[];

                            if (currentRootMaterials.length > 0 && sceneEditor && sceneEditor.setMaterialsGroup) {
                                sceneEditor.setMaterialsGroup(currentRootMaterials, '_dummy_', true);
                            }
                        }, 50);

                        // ESCI IMMEDIATAMENTE - non processare altro
                        return;
                    }

                    if (targetNode && targetNode.type === 'group') {
                        // DRAG & DROP di materiali su un GRUPPO
                        // Filtra solo i materiali (non altri gruppi)
                        const materialIds: string[] = [];
                        const materialNodesToMove: MaterialNode[] = [];

                        nodesToMove.forEach((nodeId) => {
                            const node = findNodeById(nodeId);
                            if (node && node.type === 'material' && node.data.material) {
                                // Usa la stessa logica di generazione ID dei nodi TreeComponent
                                const materialId = node.data.material._internalId || node.data.material.id;
                                if (materialId) {
                                    materialIds.push(materialId);
                                    materialNodesToMove.push(node);
                                }
                            }
                        });

                        if (materialIds.length > 0) {
                            // RIMUOVI i materiali dalla lista di quelli manualmente disassociati
                            // Quando un materiale viene spostato manualmente in un gruppo, non è più "manually ungrouped"
                            const materialNodesToUpdate = materialNodesToMove.filter((node) => {
                                const materialId = node.data.material?._internalId || node.data.material?.id;
                                if (materialId && manuallyUngroupedMaterials.has(materialId)) {
                                    setManuallyUngroupedMaterials((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.delete(materialId);
                                        return newSet;
                                    });
                                    return true;
                                }
                                return false;
                            });

                            // Aggiorna il gruppo logico in SceneEditor
                            if (sceneEditor && sceneEditor.setMaterialsGroup) {
                                // Ottieni i materiali già presenti nel gruppo
                                const existingMaterialIds = targetNode.data.materialIds || [];
                                // Combina con i nuovi materiali (evita duplicati)
                                const allMaterialIds = [...new Set([...existingMaterialIds, ...materialIds])];

                                sceneEditor.setMaterialsGroup(allMaterialIds, targetNode.name, true);

                                // Aggiorna i materialIds del gruppo
                                targetNode.data.materialIds = allMaterialIds;
                            }

                            // Lo spostamento sul bersaglio l'ha già fatto chi ha lasciato la riga: rifarlo qui era la stessa operazione due volte.
                        } else {
                            console.warn('⚠️ No valid materials to add to group');
                        }
                    } else {
                        // **GESTISCI LA DISASSOCIAZIONE DAI GRUPPI**
                        // Se i materiali vengono spostati fuori da un gruppo (targetParentId è null = root)
                        if (targetParentId === null) {
                            // PRIMA disassocia dal SceneEditor, POI sposta nel tree
                            nodesToMove.forEach((nodeId) => {
                                const node = findNodeById(nodeId);
                                const currentParentId = treeState.findParentId(nodeId);

                                if (node && node.type === 'material' && currentParentId) {
                                    // Il materiale sta uscendo da un gruppo
                                    const parentGroup = findNodeById(currentParentId);
                                    if (parentGroup && parentGroup.type === 'group') {
                                        // TRACCIA IL MATERIALE COME MANUALMENTE DISASSOCIATO
                                        // Usa la stessa logica di generazione ID dei nodi TreeComponent
                                        const materialId = node.data.material?._internalId || node.data.material?.id;
                                        if (materialId) {
                                            setManuallyUngroupedMaterials((prev) => new Set(prev.add(materialId)));
                                        }

                                        // DISASSOCIA IL MATERIALE DAL GRUPPO LOGICO nel SceneEditor
                                        // STRATEGIA: invece di solo rimuovere, spostiamo nel gruppo "_dummy_"
                                        if (sceneEditor && materialId) {
                                            if (sceneEditor.setMaterialsGroup) {
                                                // Sposta il materiale nel gruppo "_dummy_" che rappresenta materiali non raggruppati
                                                sceneEditor.setMaterialsGroup([materialId], '_dummy_', true);
                                            } else if (sceneEditor.removeMaterialGroup) {
                                                sceneEditor.removeMaterialGroup(materialId);
                                            }
                                        }

                                        // RIMUOVI IL MATERIALE dalla lista materialIds del gruppo
                                        if (parentGroup.data.materialIds && materialId) {
                                            parentGroup.data.materialIds = parentGroup.data.materialIds.filter((id) => id !== materialId);
                                        }
                                    }
                                }
                            });

                            // Lo spostamento sul bersaglio l'ha già fatto chi ha lasciato la riga: rifarlo qui era la stessa operazione due volte.

                            // FORZA UN AGGIORNAMENTO RITARDATO per permettere al SceneEditor di processare il cambio
                            setTimeout(() => {
                                // Se disponibile, ricarica i materiali dal SceneEditor per sincronizzare
                                if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                    onMaterialsUpdated(sceneEditor.getMaterials());
                                }
                            }, 100);
                            return; // Esci senza propagare ulteriori aggiornamenti
                        }

                        // I nodi mantengono la loro struttura originale e NON vengono raggruppati.
                        // Lo spostamento sul bersaglio l'ha già fatto chi ha lasciato la riga: rifarlo qui era la stessa operazione due volte.
                    }

                    // Propaga gli aggiornamenti al SceneEditor (solo per operazioni normali)
                    handleTreeDataChanged(nodes);

                    // Notifica il cambiamento (solo per operazioni normali)
                    if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                        onMaterialsUpdated(sceneEditor.getMaterials());
                    }
                }}
            />
        </SectionPanel>
    );
};

export default MaterialsEditor;
